import React, { useState, useMemo, useRef, useEffect } from "react";
import { departmentNoticesApi, facultyDepartmentApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./DepartmentNotices.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const DEPARTMENTS = [
  "General / All Students", "Computer Science", "Mathematics", "Physics", "Chemistry",
  "Biology", "English", "Commerce", "Economics",
  "History", "Political Science", "B.B.A.", "M.B.A.", "M.C.A.",
];

const NOTICE_TYPES = [
  "General", "Exam", "Holiday", "Event", "Scholarship",
  "Assignment", "Seminar", "Workshop", "Result", "Urgent",
];

const PRIORITY_META = {
  Low:    { label: "Low",    color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  Medium: { label: "Medium", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  High:   { label: "High",   color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
  Urgent: { label: "Urgent", color: "#dc2626", bg: "#fff1f2", border: "#fecaca" },
};

const STATUS_META = {
  Published: { label: "Published", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  Draft:     { label: "Draft",     color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
  Archived:  { label: "Archived",  color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const TYPE_COLORS = {
  General:     { color: "#6366f1", bg: "#eef2ff" },
  Exam:        { color: "#dc2626", bg: "#fef2f2" },
  Holiday:     { color: "#16a34a", bg: "#f0fdf4" },
  Event:       { color: "#7c3aed", bg: "#f5f3ff" },
  Scholarship: { color: "#0369a1", bg: "#f0f9ff" },
  Assignment:  { color: "#d97706", bg: "#fffbeb" },
  Seminar:     { color: "#0891b2", bg: "#ecfeff" },
  Workshop:    { color: "#9333ea", bg: "#fdf4ff" },
  Result:      { color: "#059669", bg: "#ecfdf5" },
  Urgent:      { color: "#dc2626", bg: "#fff1f2" },
};

const DEPT_COLORS = [
  ["#7c3aed", "#f5f3ff"], ["#0ea5e9", "#f0f9ff"], ["#10b981", "#ecfdf5"],
  ["#f59e0b", "#fffbeb"], ["#ec4899", "#fdf2f8"], ["#6366f1", "#eef2ff"],
  ["#14b8a6", "#f0fdfa"], ["#f97316", "#fff7ed"], ["#8b5cf6", "#f5f3ff"],
  ["#0891b2", "#ecfeff"], ["#d97706", "#fffbeb"], ["#059669", "#ecfdf5"],
  ["#dc2626", "#fef2f2"],
];
const getDeptColor = (dept) => DEPT_COLORS[(DEPARTMENTS.indexOf(dept) >= 0 ? DEPARTMENTS.indexOf(dept) : Math.abs(String(dept).length)) % DEPT_COLORS.length] || DEPT_COLORS[0];
const getDeptInitials = (dept) => dept ? dept.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() : "DN";

const fmtDate     = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const genCode     = (dept) => `NOT-${(dept || "XX").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase()}-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;

const defaultForm = {
  _id: null, Notice_Code: "",
  Notice_Title: "", Department: "", Notice_Type: "General",
  Priority: "Medium", Notice_Content: "",
  Published_Date: new Date().toISOString().slice(0, 10),
  Expiry_Date: "", Notice_PDF_URL: "",
  createdBy: "", Status: "Draft",
};

// ─── Component ────────────────────────────────────────────────────────────────
const DepartmentNotices = () => {
  const [notices, setNotices]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [dynamicDepartments, setDynamicDepartments] = useState([]);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterDept, setFilterDept]         = useState("all");
  const [filterType, setFilterType]         = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchText, setSearchText]         = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotices();
    fetchDepartmentsList();
  }, []);

  const fetchDepartmentsList = async () => {
    try {
      const response = await apiClient(facultyDepartmentApi);
      if (response.ok) {
        const result = await response.json();
        const facultiesList = result.data || (Array.isArray(result) ? result : []);
        const depts = ["General / All Students"];

        facultiesList.forEach((fac) => {
          if (fac.departments && Array.isArray(fac.departments)) {
            fac.departments.forEach((dept) => {
              if (dept.name && !depts.includes(dept.name)) {
                depts.push(dept.name);
              }
            });
          }
        });

        if (depts.length > 1) {
          setDynamicDepartments(depts);
        }
      }
    } catch (error) {
      console.error("Error fetching dynamic departments list:", error);
    }
  };

  const allDepartmentsList = useMemo(() => {
    return dynamicDepartments.length > 1 ? dynamicDepartments : DEPARTMENTS;
  }, [dynamicDepartments]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const response = await apiClient(departmentNoticesApi);
      const result = await response.json();
      if (response.ok) {
        setNotices(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching notices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut   = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => notices.filter((n) => {
    const q = searchText.toLowerCase();
    const matchSearch =
      (n.Notice_Title || "").toLowerCase().includes(q) ||
      (n.Notice_Code || "").toLowerCase().includes(q) ||
      (n.Department || "").toLowerCase().includes(q) ||
      (n.Notice_Content || "").toLowerCase().includes(q) ||
      (n.createdBy || "").toLowerCase().includes(q);
    return matchSearch &&
      (filterDept     === "all" || n.Department === filterDept) &&
      (filterType     === "all" || n.Notice_Type === filterType) &&
      (filterStatus   === "all" || n.Status     === filterStatus) &&
      (filterPriority === "all" || n.Priority   === filterPriority);
  }), [notices, searchText, filterDept, filterType, filterStatus, filterPriority]);

  const publishedCount = notices.filter((n) => n.Status === "Published").length;
  const draftCount     = notices.filter((n) => n.Status === "Draft").length;
  const archivedCount  = notices.filter((n) => n.Status === "Archived").length;
  const urgentCount    = notices.filter((n) => (n.Priority === "High" || n.Priority === "Urgent") && n.Status === "Published").length;
  const hasFilters     = filterDept !== "all" || filterType !== "all" || filterStatus !== "all" || filterPriority !== "all" || !!searchText;
  const currentDDItem  = notices.find((n) => n._id === dropdownOpenId);

  // ── Dropdown ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 215, mH = 270, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth - 12) left = window.innerWidth - mW - 12;
    if (top  + mH > window.innerHeight - 12) top = rect.top - mH - gap;
    if (top  < 12) top = 12;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  // ── Modal Helpers ──────────────────────────────────────────────────────────
  const openAddModal  = () => {
    setFormData({
      ...defaultForm,
      Notice_Code: genCode(""),
      Published_Date: new Date().toISOString().slice(0, 10),
    });
    setModalType("add");
    setDropdownOpenId(null);
  };
  const openViewModal = (n) => { setSelectedItem(n); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (n) => { 
    setFormData({ 
      ...n, 
      Published_Date: n.Published_Date ? new Date(n.Published_Date).toISOString().slice(0, 10) : "",
      Expiry_Date: n.Expiry_Date ? new Date(n.Expiry_Date).toISOString().slice(0, 10) : ""
    }); 
    setModalType("edit"); 
    setDropdownOpenId(null); 
  };
  const closeModal    = ()  => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };
  const openConfirm   = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === "Department") next.Notice_Code = genCode(value);
      return next;
    });
  };

  const handleSave = async (e, targetStatus = null) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      setLoading(true);
      const code = formData.Notice_Code || genCode(formData.Department);
      const finalStatus = targetStatus || formData.Status || "Published";
      const body = { ...formData, Notice_Code: code, Status: finalStatus };
      
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? departmentNoticesApi : `${departmentNoticesApi}/${formData._id}`;
      
      const response = await apiClient(url, { method, body: JSON.stringify(body) });
      if (response.ok) {
        fetchNotices();
        closeModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errMsg = errorData.message || errorData.error || "Failed to save notice";
        console.error("Failed to save notice:", errMsg, errorData);
        alert(`Error saving notice: ${errMsg}`);
      }
    } catch (error) {
      console.error("Error saving notice:", error);
      alert(`Error saving notice: ${error.message || "Network/Server error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setLoading(true);
      const item = notices.find(n => n._id === id);
      const response = await apiClient(`${departmentNoticesApi}/${id}`, { 
        method: "PUT", 
        body: JSON.stringify({ ...item, Status: newStatus }) 
      });
      if (response.ok) {
        fetchNotices();
        setConfirmId(null); setConfirmAction("");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${departmentNoticesApi}/${id}`, { method: "DELETE" });
      if (response.ok) {
        fetchNotices();
        setConfirmId(null); setConfirmAction("");
      }
    } catch (error) {
      console.error("Error deleting notice:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => { setFilterDept("all"); setFilterType("all"); setFilterStatus("all"); setFilterPriority("all"); setSearchText(""); };

  const isExpired = (expiryDate) => expiryDate && new Date(expiryDate) < new Date();

  return (
    <div className="dn-page">

      {/* ── Header ── */}
      <div className="dn-header">
        <div className="dn-header-left">
          <div className="dn-header-icon">📢</div>
          <div>
            <div className="dn-breadcrumb">
              <span>Department at a Glance</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <span className="dn-breadcrumb-active">Department Notices</span>
            </div>
            <h1>Department Notices</h1>
            <p>Select a department to publish, manage and track notices — exams, events, results, scholarships and more.</p>
          </div>
        </div>
        <button type="button" className="dn-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Post Notice
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="dn-stats">
        {[
          { cls: "total",     icon: "📋", label: "Total Notices", val: notices.length,  key: "all"       },
          { cls: "published", icon: "✅", label: "Published",     val: publishedCount,  key: "Published" },
          { cls: "draft",     icon: "✏️", label: "Drafts",        val: draftCount,      key: "Draft"     },
          { cls: "urgent",    icon: "🚨", label: "Urgent",        val: urgentCount,     key: "urgent"    },
          { cls: "archived",  icon: "🗄️", label: "Archived",      val: archivedCount,   key: "Archived"  },
        ].map((s) => (
          <div key={s.cls}
            className={`dn-stat-card ${s.cls} ${filterStatus === s.key || (s.key === "urgent" && filterPriority === "High") ? "active-filter" : ""}`}
            onClick={() => {
              if (s.key === "all")    { setFilterStatus("all"); setFilterPriority("all"); }
              else if (s.key === "urgent") { setFilterPriority("High"); setFilterStatus("Published"); }
              else { setFilterStatus(s.key); setFilterPriority("all"); }
            }}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content">
              <span>{s.label}</span>
              <strong>{s.val}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="dn-toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input type="text" placeholder="Search by title, code, department, content…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          {searchText && <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>}
        </div>

        <div className="toolbar-filters">
          <div className="toolbar-filter-group">
            <label>Department</label>
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
              className={`toolbar-select ${filterDept !== "all" ? "active-select" : ""}`}>
              <option value="all">All Departments</option>
              {allDepartmentsList.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div className="toolbar-filter-group">
            <label>Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className={`toolbar-select ${filterType !== "all" ? "active-select" : ""}`}>
              <option value="all">All Types</option>
              {NOTICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="toolbar-filter-group">
            <label>Priority</label>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
              className={`toolbar-select ${filterPriority !== "all" ? "active-select" : ""}`}>
              <option value="all">All Priorities</option>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="toolbar-filter-group">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className={`toolbar-select ${filterStatus !== "all" ? "active-select" : ""}`}>
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {hasFilters && (
            <button type="button" className="clear-filters-btn" onClick={clearAllFilters}>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Clear
            </button>
          )}
          <span className="result-count"><strong>{filtered.length}</strong> of <strong>{notices.length}</strong></span>
        </div>
      </div>

      {/* ── Active Filter Pills ── */}
      {hasFilters && (
        <div className="active-filter-pills">
          {searchText       && <span className="filter-pill">"{searchText}" <button type="button" onClick={() => setSearchText("")}>×</button></span>}
          {filterDept     !== "all" && <span className="filter-pill">Dept: {filterDept} <button type="button" onClick={() => setFilterDept("all")}>×</button></span>}
          {filterType     !== "all" && <span className="filter-pill">Type: {filterType} <button type="button" onClick={() => setFilterType("all")}>×</button></span>}
          {filterPriority !== "all" && <span className="filter-pill">Priority: {PRIORITY_META[filterPriority]?.label} <button type="button" onClick={() => setFilterPriority("all")}>×</button></span>}
          {filterStatus   !== "all" && <span className="filter-pill">Status: {STATUS_META[filterStatus]?.label} <button type="button" onClick={() => setFilterStatus("all")}>×</button></span>}
          <button type="button" className="pill-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* ── Notice Cards ── */}
      {loading ? (
        <div className="dn-loading">
          <div className="spinner"></div>
          <p>Loading notices...</p>
        </div>
      ) : filtered.length > 0 ? (
        <div className="dn-grid">
          {filtered.map((notice) => {
            const sm           = STATUS_META[notice.Status] || STATUS_META.Draft;
            const pm           = PRIORITY_META[notice.Priority] || PRIORITY_META.Medium;
            const tc           = TYPE_COLORS[notice.Notice_Type] || TYPE_COLORS["General"];
            const [ac, ab]     = getDeptColor(notice.Department);
            const expired      = isExpired(notice.Expiry_Date);
            return (
              <div key={notice._id} className={`dn-card ${notice.Priority === "High" || notice.Priority === "Urgent" ? "card-urgent" : ""}`}
                onClick={() => openViewModal(notice)}>

                {/* Card Top */}
                <div className="dn-card-top">
                  <div className="dn-card-dept-avatar" style={{ background: ab, color: ac }}>
                    {getDeptInitials(notice.Department)}
                  </div>
                  <div className="dn-card-top-meta">
                    <span className="dn-card-dept" style={{ color: ac }}>{notice.Department}</span>
                    <span className="dn-card-code">{notice.Notice_Code}</span>
                  </div>
                  <div className="dn-card-top-right">
                    <span className="type-badge" style={{ color: tc.color, background: tc.bg }}>
                      {notice.Notice_Type}
                    </span>
                    <button type="button" className="dots-btn"
                      onClick={(e) => handleDotsClick(e, notice._id)}>⋮</button>
                  </div>
                </div>

                {/* Title */}
                <div className="dn-card-title">{notice.Notice_Title}</div>

                {/* Content Preview */}
                <div className="dn-card-content">{notice.Notice_Content}</div>

                {/* Badges Row */}
                <div className="dn-card-badges">
                  <span className="priority-badge"
                    style={{ color: pm.color, background: pm.bg, border: `1px solid ${pm.border}` }}>
                    ● {pm.label} Priority
                  </span>
                  <span className="status-badge-sm"
                    style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.border}` }}>
                    {sm.label}
                  </span>
                  {expired && notice.Status !== "Archived" && (
                    <span className="expired-badge">Expired</span>
                  )}
                  {notice.Notice_PDF_URL && (
                    <span className="attach-badge">📎 Attachment</span>
                  )}
                </div>

                {/* Footer */}
                <div className="dn-card-footer">
                  <div className="dn-card-footer-left">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>{notice.createdBy || "—"}</span>
                  </div>
                  <div className="dn-card-footer-right">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>{fmtDate(notice.Published_Date)}</span>
                    {notice.Expiry_Date && (
                      <>
                        <span className="footer-sep">→</span>
                        <span className={expired ? "date-expired" : ""}>{fmtDate(notice.Expiry_Date)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dn-empty">
          <div className="empty-icon">📢</div>
          <h3>No notices found</h3>
          <p>{hasFilters ? "Try adjusting your filters or search term." : "No notices posted yet. Click \"Post Notice\" to get started."}</p>
          {hasFilters
            ? <button type="button" className="dn-clear-btn" onClick={clearAllFilters}>Clear Filters</button>
            : <button type="button" className="dn-add-btn" onClick={openAddModal}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Post Notice
              </button>}
        </div>
      )}

      {/* ── Three-dot Dropdown ── */}
      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown"
          style={{ position: "fixed", top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(notices.find((n) => n._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" />
            </svg>
            View Notice
          </button>
          <button type="button" onClick={() => openEditModal(notices.find((n) => n._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            Edit Notice
          </button>
          <div className="dropdown-divider" />
          {currentDDItem?.Status !== "Published" && (
            <button type="button" className="dd-publish" onClick={() => openConfirm(dropdownOpenId, "Published")}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Publish Notice
            </button>
          )}
          {currentDDItem?.Status !== "Draft" && (
            <button type="button" className="dd-draft" onClick={() => openConfirm(dropdownOpenId, "Draft")}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
              Move to Draft
            </button>
          )}
          {currentDDItem?.Status !== "Archived" && (
            <button type="button" className="dd-archive" onClick={() => openConfirm(dropdownOpenId, "Archived")}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              Archive
            </button>
          )}
          {currentDDItem?.Notice_PDF_URL && (
            <a href={currentDDItem.Notice_PDF_URL} target="_blank" rel="noreferrer" className="dd-download-link">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Attachment
            </a>
          )}
          <div className="dropdown-divider" />
          <button type="button" className="dd-delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon-box ci-${confirmAction.toLowerCase()}`}>
              {confirmAction === "delete"  && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>}
              {confirmAction === "Published" && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>}
              {confirmAction === "Draft"   && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>}
              {confirmAction === "Archived" && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>}
            </div>
            <h3>
              {confirmAction === "delete"  ? "Delete Notice?"       :
               confirmAction === "Published" ? "Publish Notice?"      :
               confirmAction === "Draft"   ? "Move to Draft?"       : "Archive Notice?"}
            </h3>
            <p>
              {confirmAction === "delete"  ? "This notice will be permanently deleted and cannot be recovered." :
               confirmAction === "Published" ? "This notice will be published and visible to all department students." :
               confirmAction === "Draft"   ? "This notice will be moved back to draft and hidden from students." :
               "This notice will be archived and no longer visible to students."}
            </p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button"
                className={`btn ${confirmAction === "delete" || confirmAction === "Archived" ? "btn-danger" : confirmAction === "Published" ? "btn-success" : "btn-secondary"}`}
                onClick={() => {
                  if      (confirmAction === "delete")  handleDelete(confirmId);
                  else if (confirmAction === "Published") handleStatusChange(confirmId, "Published");
                  else if (confirmAction === "Draft")   handleStatusChange(confirmId, "Draft");
                  else if (confirmAction === "Archived") handleStatusChange(confirmId, "Archived");
                }}>
                {confirmAction === "delete"  ? "Yes, Delete"   :
                 confirmAction === "Published" ? "Yes, Publish"  :
                 confirmAction === "Draft"   ? "Move to Draft" : "Yes, Archive"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="dn-modal" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">📢</div>
                <div>
                  <h2>{modalType === "add" ? "Post New Notice" : "Edit Notice"}</h2>
                  <span>
                    {formData.Department
                      ? `${formData.Department} · ${formData.Notice_Type}`
                      : "Select a department to begin"}
                  </span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="modal-form" onSubmit={handleSave}>
              <div className="modal-body">

                {/* ── Department Picker ── */}
                <div className="form-section">
                  <div className="form-section-label">
                    <span>🏛</span> Select Department
                  </div>
                  <div className="dept-picker-grid">
                    {allDepartmentsList.map((d) => {
                      const [ac, ab] = getDeptColor(d);
                      const selected = formData.Department === d;
                      return (
                        <button key={d} type="button"
                          className={`dept-picker-card ${selected ? "selected" : ""}`}
                          style={selected ? { borderColor: ac, background: ab } : {}}
                          onClick={() => handleChange({ target: { name: "Department", value: d } })}>
                          <span className="dpc-avatar" style={{ background: selected ? ac : "#e2e8f0", color: selected ? "#fff" : "#64748b" }}>
                            {getDeptInitials(d)}
                          </span>
                          <span className="dpc-label" style={{ color: selected ? ac : "#374151" }}>{d}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── Notice Details ── */}
                <div className="form-section">
                  <div className="form-section-label"><span>📝</span> Notice Details</div>

                  <div className="form-group full">
                    <label>Notice Title <span className="req">*</span></label>
                    <input type="text" name="Notice_Title" value={formData.Notice_Title || ""} onChange={handleChange}
                      placeholder="e.g. Mid-Term Examination Schedule – Semester 3"
                      required />
                  </div>

                  <div className="form-row-3">
                    <div className="form-group">
                      <label>Notice Type</label>
                      <select name="Notice_Type" value={formData.Notice_Type || "General"} onChange={handleChange}>
                        {NOTICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Priority</label>
                      <select name="Priority" value={formData.Priority || "Medium"} onChange={handleChange}>
                        {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Notice Code</label>
                      <input type="text" name="Notice_Code" value={formData.Notice_Code || ""} onChange={handleChange}
                        placeholder="Auto-generated" />
                    </div>
                  </div>

                  <div className="form-group full">
                    <label>Notice Content <span className="req">*</span></label>
                    <textarea name="Notice_Content" value={formData.Notice_Content || ""} onChange={handleChange}
                      placeholder="Write the full notice content here…"
                      rows="6" required />
                    <span className="field-hint">{formData.Notice_Content.length} characters</span>
                  </div>
                </div>

                {/* ── Dates & Attachment ── */}
                <div className="form-section">
                  <div className="form-section-label"><span>📅</span> Dates & Attachment</div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Publish Date <span className="req">*</span></label>
                      <input type="date" name="Published_Date" value={formData.Published_Date || ""} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Expiry Date <span className="field-opt">(optional)</span></label>
                      <input type="date" name="Expiry_Date" value={formData.Expiry_Date || ""} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Posted By (HOD / Faculty)</label>
                      <input type="text" name="createdBy" value={formData.createdBy || ""} onChange={handleChange}
                        placeholder="e.g. Dr. Rajesh Kumar" />
                    </div>
                    <div className="form-group">
                      <label>Attachment URL <span className="field-opt">(optional)</span></label>
                      <div className="url-input-wrap">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                        <input type="url" name="Notice_PDF_URL" value={formData.Notice_PDF_URL || ""} onChange={handleChange}
                          placeholder="https://…" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Status ── */}
                <div className="form-section">
                  <div className="form-section-label"><span>⚙️</span> Publication Status</div>
                  <div className="status-row">
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <label key={k}
                        className={`status-radio-card ${formData.Status === k ? "selected" : ""}`}
                        style={formData.Status === k ? { borderColor: v.color, background: v.bg } : {}}>
                        <input type="radio" name="Status" value={k}
                          checked={formData.Status === k} onChange={handleChange} />
                        <div className="src-dot" style={{ background: v.color }} />
                        <div>
                          <strong style={formData.Status === k ? { color: v.color } : {}}>{v.label}</strong>
                          <span>
                            {k === "Published" ? "Visible to all students"  :
                             k === "Draft"     ? "Saved, not published yet" :
                                                 "Hidden from students"}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <div className="mf-right">
                  {modalType === "add" && (
                    <button type="button" className="btn btn-secondary"
                      onClick={(e) => handleSave(e, "Draft")}>
                      Save as Draft
                    </button>
                  )}
                  <button type="button" className="btn btn-primary"
                    onClick={(e) => handleSave(e, "Published")}>
                    {loading ? (modalType === "add" ? "Publishing…" : "Saving…") : modalType === "add" ? "📢 Publish Notice" : "✓ Update Notice"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (() => {
        const n        = selectedItem;
        const sm       = STATUS_META[n.Status] || STATUS_META.Draft;
        const pm       = PRIORITY_META[n.Priority] || PRIORITY_META.Medium;
        const tc       = TYPE_COLORS[n.Notice_Type] || TYPE_COLORS["General"];
        const [ac, ab] = getDeptColor(n.Department);
        const expired  = isExpired(n.Expiry_Date);
        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="dn-modal view-modal" onClick={(e) => e.stopPropagation()}>

              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="view-dept-avatar" style={{ background: ab, color: ac }}>
                    {getDeptInitials(n.Department)}
                  </div>
                  <div>
                    <div className="view-dept-name" style={{ color: ac }}>{n.Department}</div>
                    <div className="view-header-badges">
                      <span className="view-code">{n.Notice_Code}</span>
                      <span className="type-badge" style={{ color: tc.color, background: tc.bg }}>{n.Notice_Type}</span>
                      <span className="priority-badge"
                        style={{ color: pm.color, background: pm.bg, border: `1px solid ${pm.border}` }}>
                        ● {pm.label}
                      </span>
                      <span className="status-badge-sm"
                        style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.border}` }}>
                        {sm.label}
                      </span>
                      {expired && <span className="expired-badge">Expired</span>}
                    </div>
                  </div>
                </div>
                <button type="button" className="modal-close" onClick={closeModal}>×</button>
              </div>

              <div className="view-body">

                {/* Title */}
                <div className="view-notice-title">{n.Notice_Title}</div>

                {/* Meta Row */}
                <div className="view-meta-row">
                  <div className="vmr-item">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>Posted by <strong>{n.createdBy || "—"}</strong></span>
                  </div>
                  <div className="vmr-item">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>Published: <strong>{fmtDate(n.Published_Date)}</strong></span>
                  </div>
                  {n.Expiry_Date && (
                    <div className={`vmr-item ${expired ? "vmr-expired" : ""}`}>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>Expires: <strong>{fmtDate(n.Expiry_Date)}</strong>{expired ? " (Expired)" : ""}</span>
                    </div>
                  )}
                  {n.createdAt && (
                    <div className="vmr-item">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>Created: <strong>{fmtDateTime(n.createdAt)}</strong></span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="view-section-label">Notice Content</div>
                <div className="view-notice-content">{n.Notice_Content}</div>

                {/* Attachment */}
                {n.Notice_PDF_URL && (
                  <>
                    <div className="view-section-label">Attachment</div>
                    <a href={n.Notice_PDF_URL} target="_blank" rel="noreferrer" className="attachment-card">
                      <div className="ac-icon">📎</div>
                      <div>
                        <strong>Download Attachment</strong>
                        <span>{n.Notice_PDF_URL}</span>
                      </div>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#7c3aed" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </a>
                  </>
                )}

                <div className="view-footer-actions">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                  <div style={{ display: "flex", gap: 10 }}>
                    {n.Status !== "Published" && (
                      <button type="button" className="btn btn-success"
                        onClick={() => { handleStatusChange(n._id, "Published"); closeModal(); }}>
                        📢 Publish Now
                      </button>
                    )}
                    <button type="button" className="btn btn-primary"
                      onClick={() => { closeModal(); openEditModal(n); }}>
                      Edit Notice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DepartmentNotices;